/* ==========================================================================
   ORW Men Collection

   - Tab button active-state toggle (all breakpoints).
   - Mobile-only (<= 749px): horizontal scroll-snap product rail with
     pagination dots that follow scroll position. Matches the Collection
     Cards pattern for UX consistency.
   ========================================================================== */

(function () {
  'use strict';

  var MOBILE_MQ = '(max-width: 749px)';

  function initTabs(section) {
    var tabs = section.querySelectorAll('.orw-men-collection__tab');
    if (!tabs.length) return;
    var panels = section.querySelectorAll('[data-orw-collection-tab-panel]');
    var dotGroups = section.querySelectorAll('[data-orw-collection-tab-dots]');

    function showPanel(key) {
      panels.forEach(function (p) {
        var on = p.getAttribute('data-orw-collection-tab-panel') === key;
        p.classList.toggle('is-hidden', !on);
        if (on) {
          p.removeAttribute('hidden');
          // Reset scroll for mobile rail
          if (p.scrollLeft) p.scrollLeft = 0;
        } else {
          p.setAttribute('hidden', '');
        }
      });
      dotGroups.forEach(function (d) {
        var on = d.getAttribute('data-orw-collection-tab-dots') === key;
        d.classList.toggle('is-hidden', !on);
        if (on) d.removeAttribute('hidden');
        else d.setAttribute('hidden', '');
      });
      // Re-bind rail/dots for the now-active panel
      initRail(section);
    }

    // Explore All follows the selected tab (per-tab link, else its collection)
    function updateExploreLinks(tab) {
      var url = tab.getAttribute('data-orw-explore-url');
      if (!url) return;
      section.querySelectorAll('[data-orw-explore-all]').forEach(function (a) {
        a.setAttribute('href', url);
      });
    }

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        tabs.forEach(function (t) {
          t.classList.remove('orw-men-collection__tab--active');
          t.setAttribute('aria-selected', 'false');
        });
        tab.classList.add('orw-men-collection__tab--active');
        tab.setAttribute('aria-selected', 'true');
        showPanel(tab.getAttribute('data-orw-collection-tab'));
        updateExploreLinks(tab);
      });
    });
  }

  function initRail(section) {
    // Pick the currently-visible panel (the one without `hidden`)
    var tracks = section.querySelectorAll('[data-orw-men-collection-track]');
    var track = null;
    for (var ti = 0; ti < tracks.length; ti++) {
      if (!tracks[ti].hasAttribute('hidden')) { track = tracks[ti]; break; }
    }
    if (!track) track = tracks[0] || null;
    if (!track) return;

    var activeKey = track.getAttribute('data-orw-collection-tab-panel');
    var dotsEl = section.querySelector(activeKey
      ? '[data-orw-collection-tab-dots="' + activeKey + '"]'
      : '[data-orw-collection-tab-dots]');
    var dots = Array.prototype.slice.call(
      (dotsEl || section).querySelectorAll('[data-orw-men-collection-dot]')
    );
    if (dots.length === 0) return;

    var cards = Array.prototype.slice.call(
      track.querySelectorAll('.orw-product-card')
    );
    if (cards.length === 0) return;

    var mql = window.matchMedia(MOBILE_MQ);
    var scrollHandler = null;

    function setActive(index) {
      for (var i = 0; i < dots.length; i++) {
        var on = i === index;
        dots[i].classList.toggle('is-active', on);
        dots[i].setAttribute('aria-selected', on ? 'true' : 'false');
      }
    }

    function onScroll() {
      var trackRect = track.getBoundingClientRect();
      // scroll-padding-inline-start: 16px in CSS -> snap anchor at trackLeft + 16
      var anchorX = trackRect.left + 16;
      var closestIdx = 0;
      var closestDist = Infinity;
      for (var i = 0; i < cards.length; i++) {
        var dist = Math.abs(cards[i].getBoundingClientRect().left - anchorX);
        if (dist < closestDist) {
          closestDist = dist;
          closestIdx = i;
        }
      }
      // Clamp to dot count (more products than dots would miss)
      if (closestIdx >= dots.length) closestIdx = dots.length - 1;
      setActive(closestIdx);
    }

    function scrollToIndex(index) {
      var card = cards[index];
      if (!card) return;
      var trackRect = track.getBoundingClientRect();
      var cardRect = card.getBoundingClientRect();
      var target = track.scrollLeft + (cardRect.left - trackRect.left) - 16;
      track.scrollTo({ left: target, behavior: 'smooth' });
    }

    function attach() {
      if (scrollHandler) return;
      scrollHandler = onScroll;
      track.addEventListener('scroll', scrollHandler, { passive: true });
      for (var i = 0; i < dots.length; i++) {
        (function (idx) {
          dots[idx].addEventListener('click', function (e) {
            e.preventDefault();
            scrollToIndex(idx);
          });
        })(i);
      }
      onScroll();
    }

    function detach() {
      if (!scrollHandler) return;
      track.removeEventListener('scroll', scrollHandler);
      scrollHandler = null;
    }

    function sync() {
      if (mql.matches) {
        attach();
      } else {
        detach();
        setActive(0);
      }
    }

    sync();

    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', sync);
    } else if (typeof mql.addListener === 'function') {
      mql.addListener(sync);
    }
  }

  /* Drag-to-scroll for mouse-only input devices.
     - Touch and stylus skip this handler -- native browser overflow
       scrolling already gives them the right inertia + snap.
     - For mouse: pointerdown captures startX + startScrollLeft, pointermove
       writes scrollLeft synchronously every frame, pointerup releases.
     - The .is-dragging class swaps cursor to grabbing and turns off
       scroll-snap + scroll-behavior so per-frame writes track the cursor
       1:1 instead of fighting CSS-queued micro-animations.
     - If the user moved more than DRAG_THRESHOLD px, the next click is
       suppressed in capture phase so the drag doesn't accidentally open
       a product page. */
  function initDragScroll(section) {
    var track = section.querySelector('[data-orw-men-collection-track]');
    if (!track) return;

    var DRAG_THRESHOLD = 5;
    var isDown = false;
    var pointerId = null;
    var startX = 0;
    var startScrollLeft = 0;
    var dragDistance = 0;
    var didDrag = false;

    function onPointerDown(e) {
      if (e.pointerType === 'touch') return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      // Skip drag-scroll when the press starts on an interactive control
      // inside a card (wishlist heart, add-to-cart, image nav). Pointer
      // capture would otherwise redirect pointerup to the track and the
      // button's native click would never fire.
      if (e.target.closest('button')) return;
      isDown = true;
      pointerId = e.pointerId;
      startX = e.clientX;
      startScrollLeft = track.scrollLeft;
      dragDistance = 0;
      didDrag = false;
      // NOTE: defer setPointerCapture + .is-dragging until actual movement
      // exceeds DRAG_THRESHOLD. Capturing on every pointerdown redirects
      // the subsequent click to the track and prevents card link navigation.
    }

    function onPointerMove(e) {
      if (!isDown || e.pointerId !== pointerId) return;
      var dx = e.clientX - startX;
      dragDistance = Math.abs(dx);
      if (!didDrag && dragDistance > DRAG_THRESHOLD) {
        didDrag = true;
        track.classList.add('is-dragging');
        try { track.setPointerCapture(pointerId); } catch (err) {}
      }
      if (didDrag) {
        track.scrollLeft = startScrollLeft - dx;
        e.preventDefault();
      }
    }

    function endDrag(e) {
      if (!isDown || (e && e.pointerId !== pointerId)) return;
      isDown = false;
      if (didDrag) {
        try { track.releasePointerCapture(pointerId); } catch (err) {}
        track.classList.remove('is-dragging');
      }
      pointerId = null;
      // Native CSS scroll-snap (proximity) + scroll-behavior smooth snap
      // softly to the nearest card now that drag is over.
    }

    function onClickCapture(e) {
      if (didDrag) {
        e.preventDefault();
        e.stopPropagation();
        didDrag = false;
      }
    }

    function onDragStart(e) {
      // Block native image / link drag-ghost so it doesn't fight the
      // pointer drag-to-scroll.
      e.preventDefault();
    }

    track.addEventListener('pointerdown', onPointerDown);
    track.addEventListener('pointermove', onPointerMove);
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('pointerleave', endDrag);
    track.addEventListener('click', onClickCapture, true);
    track.addEventListener('dragstart', onDragStart);
  }

  /* Hidden tabs' cards are lazy so they don't slow the first load. Once the
     page has loaded, fetch them in the background so a tab switch is instant. */
  function warmHiddenTabs(section) {
    var run = function() {
      var imgs = section.querySelectorAll('[data-orw-collection-tab-panel][hidden] img[loading="lazy"]');
      for (var i = 0; i < imgs.length; i++) imgs[i].loading = 'eager';
    };
    if (document.readyState === 'complete') {
      run();
    } else {
      window.addEventListener('load', function() { setTimeout(run, 1500); }, { once: true });
    }
  }

  function initAll() {
    var sections = document.querySelectorAll('[data-orw-men-collection], .orw-men-collection');
    for (var i = 0; i < sections.length; i++) {
      initTabs(sections[i]);
      initRail(sections[i]);
      initDragScroll(sections[i]);
      warmHiddenTabs(sections[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
